import { Provider as ReduxProvider } from 'react-redux'
import App from 'next/app'
import type { AppContext, AppProps, NextWebVitalsMetric } from 'next/app'
import { appWithTranslation } from 'next-i18next'

import { wrapper } from '../redux/store'
import { start } from 'utils/bugsnag'
import nextI18NextConfig from '../../next-i18next.config'
import { InternalAnalyticsProvider } from 'utils/internalAnalytics'
import { OptimizelyFeatureFlags } from 'models/Optimizely'
import { useOptimizelyPageLifecycle } from 'utils/optimizely'

const isDevEnv =
  process.env.NEXT_PUBLIC_APP_ENV === 'local' || process.env.NEXT_PUBLIC_APP_APP_ENV === 'dev'

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (isDevEnv) console.info(metric)
}

// Initialize Bugsnag
start()

type MyAppProps = AppProps & { nonce: string }

const MyApp = ({ Component, ...rest }: MyAppProps) => {
  const { store, props } = wrapper.useWrappedStore(rest)
  const { pageProps } = props

  const isMParticleEnabled =
    pageProps.optimizelyContext?.featureFlags[OptimizelyFeatureFlags.classy_mparticle].enabled

  // * Setup custom Optimizely for supported NPO pages
  useOptimizelyPageLifecycle(pageProps?.oid, pageProps?.orgOptimizelyProjectId)

  return (
    <InternalAnalyticsProvider
      appEnv={process.env.APP_ENV as string}
      mParticleApiKey={process.env.MPARTICLE_API_KEY as string}
      isMParticleEnabled={isMParticleEnabled}
      isOrgOptimizelyEnabled={!!pageProps?.orgOptimizelyProjectId}
    >
      <ReduxProvider store={store}>
        <Component {...pageProps} />
      </ReduxProvider>
    </InternalAnalyticsProvider>
  )
}

MyApp.getInitialProps = async (context: AppContext) => {
  const ctx = await App.getInitialProps(context)
  const nonce = context.ctx.req?.headers['x-nonce']

  return { ...ctx, nonce }
}

export default appWithTranslation(MyApp, nextI18NextConfig)
